// src/pages/markets/board/api/useSeries.ts

import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchBoardSeries, MAX_SERIES_MARKETS } from './boardApi';
import { boardKeys, idsKey } from './boardKeys';
import type { MarketSeries } from './boardTypes';

export interface UseSeriesOptions {
    marketIds: string[];
    window: string;
    resolution?: string | null;
    enabled?: boolean;
}

const EMPTY = new Map<string, MarketSeries>();

// Batched on purpose: a whole column can switch to trend mode on one request
// instead of thirty.
export function useSeries(opts: UseSeriesOptions) {
    const { window, resolution = null, enabled = true } = opts;
    const ids = opts.marketIds.slice(0, MAX_SERIES_MARKETS);
    const active = enabled && ids.length > 0;

    const query = useQuery({
        queryKey: boardKeys.series(idsKey(ids), window, resolution),
        queryFn: ({ signal }) =>
            fetchBoardSeries(ids, window, resolution, signal),
        enabled: active,
        placeholderData: keepPreviousData,
        staleTime: 55_000,
    });

    const byMarket = useMemo(() => {
        if (!query.data) return EMPTY;
        return new Map(query.data.series.map(s => [s.market_id, s]));
    }, [query.data]);

    return {
        byMarket,
        window: query.data?.window ?? window,
        resolution: query.data?.resolution ?? resolution,
        isFetching: query.isFetching,
        isLoading: active && query.isLoading,
        error: query.error ?? null,
    };
}

// The single-market fallback for a card that stepped off the market the column
// batch covers. Same query key shape, so a second card on the same market
// reuses the cached response instead of firing again.
export function useCardSeries(
    marketId: string | null,
    window: string,
    enabled: boolean
): {
    series: MarketSeries | null;
    isLoading: boolean;
    /** Something answered for this market, even if the answer was nothing. */
    hasData: boolean;
    error: Error | null;
} {
    const ids = useMemo(() => (marketId ? [marketId] : []), [marketId]);

    const query = useQuery({
        queryKey: boardKeys.series(idsKey(ids), window, null),
        queryFn: ({ signal }) => fetchBoardSeries(ids, window, null, signal),
        enabled: enabled && ids.length > 0,
        placeholderData: keepPreviousData,
        staleTime: 55_000,
    });

    return {
        series: query.data?.series?.[0] ?? null,
        isLoading: enabled && ids.length > 0 && query.isLoading,
        hasData: query.data !== undefined,
        error: (query.error as Error) ?? null,
    };
}
